import React, { useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import { components as selectComponents } from 'react-select'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome/index.es'
import { faExclamationCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import 'src/components/inputs/Select/CustomSelect.scss'

import IconButton from 'src/components/buttons/IconButton'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { onCreateClientStep } from 'src/actions/clients.actions'
import { setModalCreateClient } from 'src/actions/utilities.actions'
import { showAlert } from 'src/actions/alert.actions'

import { selectIdentificationTypes } from 'src/selectors/company.selector'
import { selectCurrentUser } from 'src/selectors/user.selector'

import { identificationTypesEnum } from 'src/enums/identificationTypes'

import { SimpleClient, useClientsPaginated } from './useClientsPaginated'

interface ClientOption extends SimpleClient {
  value: number
  label: string
}

interface IProps {
  /** @deprecated El loading ahora es local al componente; se ignora. */
  actionType?: string
  label?: string
  value: any
  identifications?: { [key in identificationTypesEnum]?: IdentificationOption }
  info?: string
  className?: string
  onChange: (value, identifications?) => void
  onCreateOption?: (value) => void
  disabled?: boolean
  showAllOption?: boolean
  allClients?: boolean
  /** Filtra clientes visibles para ese usuario. Tiene prioridad sobre allClients. */
  attendant?: number
  /** @deprecated La carga es lazy (al abrir el menú); se ignora. */
  init?: boolean
  noCreate?: boolean
  multi?: boolean
  required?: boolean
  error?: string
}

const allOption = { value: null, label: '- Todos -' }

const selectStyle = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
}

// MenuList propio que pagina en onScroll. El ScrollCaptor interno de
// react-select v3 no dispara onMenuScrollToBottom de forma fiable en desktop;
// escuchar onScroll directamente sobre la lista es robusto. onLoadMore trae
// su propio guard (hasMore/!loading), así que llamarlo de más no hace daño.
const PaginatedMenuList = props => {
  const { onLoadMore } = props.selectProps
  return (
    <selectComponents.MenuList
      {...props}
      innerProps={{
        ...props.innerProps,
        onScroll: e => {
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
          if (scrollHeight - scrollTop - clientHeight < 32) onLoadMore?.()
        },
      }}
    />
  )
}

/**
 * Selector de clientes con búsqueda server-side e infinity scroll
 * (GET /api/clients/simple paginado, ATL-323). La lista vive en estado
 * local del componente, no en Redux.
 */
const SelectClient = ({
  label = 'Seleccionar cliente',
  value,
  identifications = {},
  info,
  className = '',
  onChange,
  onCreateOption,
  disabled,
  showAllOption,
  allClients = false,
  attendant,
  noCreate = false,
  multi = false,
  required = false,
  error,
}: IProps) => {
  const dispatch = useAppDispatch()

  const identificationTypes = useAppSelector(selectIdentificationTypes)
  const user = useAppSelector(selectCurrentUser)

  const attendantID: number =
    attendant !== undefined ? attendant : allClients ? null : user.id

  const { clients, searching, loading, onMenuOpen, onLoadMore, onSearchChange } =
    useClientsPaginated({ attendant: attendantID })

  const [inputValue, setInputValue] = useState('')

  const options: ClientOption[] = clients.map(client => ({
    ...client,
    value: client.id,
    label: `${client.code || ''} - ${client.companyName}`,
  }))
  if (showAllOption && !searching) options.unshift(allOption as ClientOption)

  const isLocked = (client): boolean => {
    if (!client?.locked) return false
    dispatch(
      showAlert({
        title: 'El cliente no tiene autorización de ventas',
        text: `Razón: ${
          client.lockMessage || 'Sin razón'
        }\nContáctese con el administrador`,
        type: 'error',
        confirmButtonText: 'Cerrar',
        confirmButtonColor: '#B35796',
      }),
    )
    return true
  }

  const handleChange = (newValue, actionMeta) => {
    if (multi) {
      if (actionMeta?.action === 'select-option' && isLocked(actionMeta.option)) return
      onChange(newValue)
      return
    }

    if (isLocked(newValue)) return

    const clientIdentifications = {}
    identificationTypes.forEach(
      identificationType =>
        (clientIdentifications[identificationType.id] = {
          identificationTypeId: identificationType.id,
          value: '',
        }),
    )
    newValue.identifications?.forEach(
      identification =>
        (clientIdentifications[identification.identificationTypeId] = identification),
    )

    onChange(newValue, clientIdentifications)
  }

  return (
    <div
      className={`input-wrapper has-feedback column ${className} mt-3`}
      style={{ margin: 0 }}>
      <label className={'ftf-form-label left'}>
        {required && <span style={{ color: 'red', marginRight: 2 }}>&nbsp;* </span>}
        {label} &nbsp;
        {info ? (
          <label>
            {' '}
            <IconButton
              tooltip={info}
              color={'rgba(34, 96, 149, 0.75)'}
              icon={faInfoCircle}
              size={'sm'}
            />{' '}
          </label>
        ) : (
          <label> &nbsp; </label>
        )}
      </label>
      <CreatableSelect
        className="basic-single"
        menuPortalTarget={document.body}
        styles={selectStyle}
        isClearable={false}
        isDisabled={disabled}
        isLoading={loading}
        isMulti={multi}
        value={value}
        options={options}
        // El filtrado es server-side: las opciones ya vienen filtradas.
        filterOption={() => true}
        inputValue={inputValue}
        onInputChange={(input, { action }) => {
          if (action === 'input-change') {
            setInputValue(input)
            onSearchChange(input)
          } else if (action === 'menu-close') {
            setInputValue('')
            onSearchChange('')
          }
        }}
        onMenuOpen={onMenuOpen}
        // Dos disparadores hacia el mismo onLoadMore (idempotente, con guard en
        // el hook): el ScrollCaptor de react-select (forzado con
        // captureMenuScroll, que por defecto se apaga en entornos touch) y un
        // onScroll propio en el MenuList como respaldo.
        components={{ MenuList: PaginatedMenuList }}
        onLoadMore={onLoadMore}
        onMenuScrollToBottom={onLoadMore}
        captureMenuScroll
        onChange={handleChange}
        isValidNewOption={inputItem => !noCreate && inputItem.trim().length > 0}
        formatCreateLabel={inputItem => `Nuevo cliente: ${inputItem}`}
        onCreateOption={name => {
          if (noCreate) return
          dispatch(setModalCreateClient({ show: true, onCreateOption }))
          dispatch(
            onCreateClientStep({
              companyName: name,
              businessName: name,
              identifications,
              email: value?.email,
              address: value?.address ?? 'Ciudad',
            }),
          )
        }}
        placeholder={'Buscar o seleccionar'}
        loadingMessage={() => 'Cargando...'}
        noOptionsMessage={() => (loading ? 'Cargando...' : 'Sin resultados')}
      />
      {error && (
        <div className="input-error">
          <FontAwesomeIcon icon={faExclamationCircle} /> {error}
        </div>
      )}
    </div>
  )
}

export default SelectClient
